import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, CircularProgress, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getStudent } from './requests/getStudent';
import { getStudentSelf } from './requests/getStudentSelf';
import { getTab } from './requests/getTab';
import { registerSecondaryWebsocket } from './requests/registerSecondaryWebsocket';
import { videoStateChanged } from './requests/videoStateChanged';
import { SyncedVideoPlayer, type VideoPlayerState } from './SyncedVideoPlayer';
import { VideoToolbar } from './VideoToolbar';
import { useSocket } from '../../context/SocketContext';
import { usePrevious } from '../../utils/usePrevious';
import ErrorModal from '../ErrorModal';

const Container = styled(Box)`
    width: 100vw;
    height: 100vh;
    overflow: hidden;
    display: flex;
    flex-flow: column;
`;
const StyledTypography = styled(Typography)`
    color: black;
`;

export interface PlaybackContentProps {
    playbackInfo: {
        tabId: string;
        classSessionUuid: string;
        meetingId: number;
        sourceZoomId?: string;
    };
    // one of these is needed
    userUuid?: string;
    tabData?: object;
}

interface LastEvent {
    event_timestamp: number;
    playback_rate: number;
    state: number;
    timestamp: number;
}

export const PlaybackContent = (props: PlaybackContentProps) => {
    const { playbackInfo, userUuid, tabData } = props;
    const { t } = useTranslation('playvideo');

    const [isController, setIsController] = useState<null | boolean>(null);
    const [controlType, setControlType] = useState<'teacher' | 'student' | null>(null);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [sharedVideoState, setSharedVideoState] = useState<VideoPlayerState>();
    const [sharerName, setSharerName] = useState<string | null>(null);
    const [videoVolume, setVideoVolume] = useState(0.5);
    const [error, setError] = useState<string | null>(null);
    const { connectionId, addMessageHandler, removeMessageHandler, status: socketStatus } = useSocket();

    const sendVideoState = useCallback(
        (state: VideoPlayerState) => {
            if (!playbackInfo || !videoUrl) {
                return;
            }

            if (!userUuid) {
                console.log('[PlaybackContent] user uuid missing, unable to send video state');
                return;
            }

            videoStateChanged({
                // Typescript complaints here because `playbackInfo.sourceZoomId` is optional, but if there's no user_uuid (checked above)
                // we're in interactive player and video state will never be sent anyway.
                ...(playbackInfo as any),
                userUuid,
                videoId: videoUrl,
                timestamp: state.timestamp,
                playing: state.playing,
                playbackRate: state.playbackRate
            });
        },
        [userUuid, playbackInfo, videoUrl]
    );

    const setStateFromLastEvent = useCallback((lastVideoEvent: LastEvent) => {
        // if the video is not playing, we go straight to the timestamp it was paused at
        if (lastVideoEvent.state !== 1) {
            console.log('[PlaybackContent] Last event update: Video is not playing', lastVideoEvent);
            setSharedVideoState({
                playing: false,
                playbackRate: lastVideoEvent.playback_rate,
                timestamp: lastVideoEvent.timestamp
            });

            return;
        }

        // the video is playing, we take the time the last event was sent (event_timestamp) into consideration
        const currentTimestamp = new Date().getTime();
        const timeSinceEvent = Math.round((currentTimestamp - lastVideoEvent.event_timestamp) / 1000);

        // also need to adjust the time since the event was dispatched by the playback rate of the video
        const adjustedTimeSinceEvent = timeSinceEvent * (lastVideoEvent.playback_rate || 1);

        const timestamp = lastVideoEvent.timestamp + adjustedTimeSinceEvent;

        console.log('[PlaybackContent] Last event update: Video was playing, calculated new timestamp', {
            lastVideoEvent,
            adjustedTimeSinceEvent,
            timestampToSet: timestamp
        });

        setSharedVideoState({
            playing: true,
            playbackRate: lastVideoEvent.playback_rate,
            timestamp
        });
    }, []);

    const refreshVideoState = useCallback(async () => {
        const { meetingId, classSessionUuid, tabId } = playbackInfo;

        const tabResult = await getTab(meetingId, tabId, classSessionUuid, userUuid);
        const lastEvent = tabResult.tab?.data?.last_event as LastEvent;

        if (!lastEvent) {
            console.warn(
                '[PlaybackContent] Found no last event for tab! This is probably a bug. Setting playback to paused at 0s.'
            );

            setSharedVideoState({
                playing: false,
                playbackRate: 1.0,
                timestamp: 0
            });

            return;
        }

        setStateFromLastEvent(lastEvent);
    }, [playbackInfo, userUuid, setStateFromLastEvent]);

    // initial useEffect that loads info about the video
    const initialLoadCompletedRef = useRef(false);
    useEffect(() => {
        if (!playbackInfo || initialLoadCompletedRef.current) {
            return;
        }

        if (!userUuid && !tabData) {
            console.log('[PlaybackContent] Missing both user uuid and tab data!');
        }

        const loadTabInfo = async () => {
            let tabResult;
            let hasMyStudentLaunchedTab = false;

            const { meetingId, tabId, classSessionUuid } = playbackInfo;

            if (tabData) {
                // allow passing tab data from query parameters to support interactive playback
                tabResult = tabData;
            } else {
                try {
                    tabResult = (await getTab(meetingId, tabId, classSessionUuid, userUuid)).tab;
                } catch (e) {
                    console.error('[PlaybackContent] error while retrieving current tab, unrecoverable', e);
                    setError(t('playback-error-generic'));

                    return;
                }
            }

            // if we don't have user uuid, we're coming from interactive playback - my student has never launched the tab
            if (userUuid) {
                try {
                    const selfResult = await getStudentSelf(meetingId, userUuid);
                    hasMyStudentLaunchedTab = String(selfResult.student?.student_id) === String(tabResult?.student_id);
                } catch (e) {
                    console.log('[PlaybackContent] Error while retrieving self student, probably not enrolled', e);
                }
            }

            const videoControlType = tabResult?.data?.control_type ?? 'teacher';
            const isMyStudentController = videoControlType === 'teacher' && hasMyStudentLaunchedTab;

            setIsController(isMyStudentController);

            // if we're coming from interactive playback, enforce self control because we won't be able to connect the user
            // to websocket (user_uuid is missing)
            setControlType(userUuid ? videoControlType : 'student');

            const url = tabResult?.data.video_id;

            if (!url) {
                console.error('[PlaybackContent] Unable to find video id.', tabResult.data);
                setError(t('playback-error-generic'));
                return;
            }

            setVideoUrl(url);

            if (userUuid && videoControlType === 'teacher' && !isMyStudentController && tabResult?.student_id) {
                // someone else is controlling this playback, update the playback to match others
                const lastEvent = tabResult?.data?.last_event;

                if (lastEvent) {
                    setStateFromLastEvent(lastEvent);
                }

                // get the controller's name
                try {
                    const sharingStudent = await getStudent(meetingId, tabResult?.student_id, userUuid);
                    setSharerName(sharingStudent.student?.user_name);
                } catch (e) {
                    console.log('[PlaybackContent] Unable to find user sharing this video.', e);
                }
            }

            initialLoadCompletedRef.current = true;
        };

        loadTabInfo();
    }, [playbackInfo, userUuid, tabData, setStateFromLastEvent, t]);

    const websocketRegisteredRef = useRef(false);
    const previousConnectionId = usePrevious(connectionId);
    useEffect(() => {
        if (websocketRegisteredRef.current && connectionId === previousConnectionId) {
            // already registered
            return;
        }

        if (!connectionId) {
            // not connected yet
            return;
        }

        if (controlType !== 'teacher') {
            // only register websocket on shared playback
            return;
        }

        if (userUuid && playbackInfo.meetingId) {
            // register the secondary websocket
            registerSecondaryWebsocket(playbackInfo.meetingId, connectionId, userUuid);
            websocketRegisteredRef.current = true;
        }
    }, [connectionId, userUuid, playbackInfo.meetingId, controlType, previousConnectionId]);

    // Listen for websocket messages of the shared video state changing
    useEffect(() => {
        if (!addMessageHandler || !removeMessageHandler) {
            return;
        }

        const handler = (funcName, data) => {
            if (funcName === 'video_state_changed') {
                if (data.video_id !== videoUrl) {
                    // there can be multiple videos playing at the same time
                    return;
                }

                setSharedVideoState({
                    playing: data.state === 1,
                    playbackRate: data.playback_rate ?? 1,
                    timestamp: data.timestamp
                });
            }
        };

        addMessageHandler(handler);

        return () => {
            removeMessageHandler(handler);
        };
    }, [videoUrl, addMessageHandler, removeMessageHandler]);

    // handle websocket disconnects/reconnects
    const socketDisconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const previousSocketStatus = usePrevious(socketStatus);
    useEffect(() => {
        // we only care about teacher playback, in self-control we ignore websocket anyway
        if (controlType !== 'teacher') {
            return;
        }

        if (socketStatus === 'disconnected' || socketStatus === 'reconnect-timeout') {
            socketDisconnectTimeoutRef.current = setTimeout(() => {
                setSharedVideoState((current) => ({ ...current, playing: false }) as VideoPlayerState);
            }, 5000);
        }

        if (socketStatus === 'connected') {
            if (socketDisconnectTimeoutRef.current) {
                clearTimeout(socketDisconnectTimeoutRef.current);
            }

            if (previousSocketStatus === 'disconnected') {
                // make sure we refresh the video state upon websocket reconnect
                refreshVideoState();
            }
        }
    }, [socketStatus, previousSocketStatus, refreshVideoState, controlType]);

    if (error) {
        return (
            <Container>
                <ErrorModal
                    open
                    onTryAgain={() => window.location.reload()}
                    onClose={() => null}
                    hideClose
                    title={t('playback-error-title')}
                >
                    {error}
                </ErrorModal>
            </Container>
        );
    }

    if (!videoUrl || !controlType || isController === null) {
        return (
            <Container alignItems="center" justifyContent="center">
                <CircularProgress disableShrink size={60} sx={{ marginBottom: '24px' }} />
                <StyledTypography>{t('playback-loading')}</StyledTypography>
            </Container>
        );
    }

    const canControlVideo = isController || controlType === 'student';

    // support for legacy videos with just the youtube video id
    const urlBackwardsCompat =
        videoUrl.startsWith('https://') || videoUrl.startsWith('http://')
            ? videoUrl
            : `https://www.youtube.com/watch?v=${videoUrl}`;

    return (
        <Container>
            <VideoToolbar
                currentVolume={videoVolume}
                onVolumeUpdate={canControlVideo ? undefined : (event, value) => setVideoVolume(value as number)}
                // if the user can control the video or they don't have user uuid (= interactive playback)
                // we disable the option to refresh the video state.
                onRefreshVideoState={canControlVideo || !userUuid ? undefined : refreshVideoState}
                isPaused={!canControlVideo && sharedVideoState?.playing === false}
            >
                {isController && t('playback-controller-me')}
                {controlType === 'teacher' &&
                    !isController &&
                    sharerName &&
                    t('playback-controller-other', { sharerName, interpolation: { escapeValue: false } })}
                {controlType === 'teacher' && !isController && !sharerName && t('playback-controller-unknown')}
                {controlType === 'student' && t('playback-controller-everyone')}
            </VideoToolbar>
            <SyncedVideoPlayer
                videoUrl={urlBackwardsCompat}
                isController={isController}
                controlType={controlType}
                onControllerStateUpdate={sendVideoState}
                onRefreshVideoState={refreshVideoState}
                sharedState={sharedVideoState}
                sharerName={sharerName || t('playback-unknown-sharer-name')}
                width="100%"
                height="100%"
                volume={videoVolume}
            />
        </Container>
    );
};
